import React, { useState } from 'react';
import { useAuth } from '@/lib';
import { ContinueWithGoogle } from '@orbit/ui';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { styled, useTheme } from '@mui/material/styles';
import { ParticleBackground } from '../components/ParticleBackground';
import { RocketShip } from '../components/RocketShip';

const BORDER_RADIUS = 10;

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: BORDER_RADIUS,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0px 4px 20px rgba(0,0,0,0.10)',
}));

export const LoginPage: React.FC = () => {
  const theme = useTheme();
  const { loginWithProvider, login, signup, loading, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isEmailSigningIn, setIsEmailSigningIn] = useState(false);
  const [isSignup, setIsSignup] = useState(false);

  React.useEffect(() => {
    if (error) setSnackbarOpen(true);
  }, [error]);

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await loginWithProvider({ id: 'google', name: 'Google' });
    } catch (error: any) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.message?.toLowerCase().includes('popup closed')
      ) {
        setIsSigningIn(false);
        return;
      }
      console.error('Google sign-in error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsEmailSigningIn(true);
    try {
      if (!email || !password) {
        setFormError('Please enter both email and password.');
        setIsEmailSigningIn(false);
        return;
      }
      const result = isSignup
        ? await signup({ email, password })
        : await login({ email, password });
      if (!result.success && result.error) {
        setFormError(result.error.message);
      }
    } catch (err: any) {
      setFormError(err?.message || 'An error occurred.');
    } finally {
      setIsEmailSigningIn(false);
    }
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleToggleMode = () => {
    setIsSignup((prev) => !prev);
    setFormError(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#1D233E',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <ParticleBackground className="particles-bg" />
      <RocketShip />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 2,
          px: { xs: 1, sm: 0 },
        }}
      >
        <StyledCard
          sx={{
            maxWidth: { xs: '100%', sm: 400 },
            width: '100%',
            p: { xs: 2, sm: 3 },
            boxSizing: 'border-box',
            minWidth: 0,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                color="text.primary"
                align="center"
                fontSize={{ xs: '2rem', sm: '2.25rem' }}
              >
                Welcome to Orbit
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                gutterBottom
                align="center"
                fontSize={{ xs: '1rem', sm: '1.125rem' }}
              >
                {`Let's build something worth launching`}
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleEmailAuth}
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                fullWidth
                required
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
                required
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              {formError && <Alert severity="error">{formError}</Alert>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isEmailSigningIn}
                size={window.innerWidth < 600 ? 'medium' : 'large'}
              >
                {isEmailSigningIn
                  ? isSignup
                    ? 'Creating Account...'
                    : 'Signing in...'
                  : isSignup
                    ? 'Create Account'
                    : 'Sign in'}
              </Button>
            </Box>

            <Divider
              sx={{
                my: 3,
                color: theme.palette.text.primary,
                '&::before, &::after': {
                  borderColor: theme.palette.text.primary,
                },
              }}
            >
              or
            </Divider>

            <ContinueWithGoogle onClick={handleGoogleSignIn} disabled={loading || isSigningIn}>
              {isSigningIn
                ? 'Signing in...'
                : isSignup
                  ? 'Sign up with Google'
                  : 'Continue with Google'}
            </ContinueWithGoogle>
          </CardContent>
        </StyledCard>
        <Box sx={{ mt: 2 }}>
          <Link
            component="button"
            variant="body2"
            color="primary"
            underline="hover"
            onClick={handleToggleMode}
            sx={{ fontWeight: 500 }}
          >
            {isSignup ? 'Back to login' : 'I already have an account'}
          </Link>
        </Box>
      </Box>
      <Snackbar
        open={!!error && snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {error?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
